import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import styled from 'styled-components';
import query from 'syf-component-library/const/mediaQueries';
import { Stack } from 'syf-component-library/ui/spacing';
import useWindowSize from 'syf-component-library/ui/customHooks/useWindowWidth';
import { useProfileQuery } from '@syf-api-library/profile';
import Link from 'syf-component-library/ui/atoms/Link';
import { t } from '../../translations/getTranslation';
import ModalImage from '../../components/ModalImage';
import { InlineCtaButton } from '../../components/Button';
import { IMAGE_MAP, getAssetUrl } from '../../const/imageRoutes';
import useConfig from '../../context/config.context';
import useSteps from '../../context/steps.context';
import { formatAddress } from '../../helpers/format';
import useCardAccessLink from '../../hooks/useCardAccessLink';
const HeadingWrapper = styled.div `
  background-color: ${({ theme }) => theme.colors.turquoiseLightest};
  padding: ${({ theme }) => `${theme.spacing.large}px ${theme.spacing.base}px`};

  @media ${query.greaterThanMedium} {
    display: flex;
    align-items: center;
    padding: ${({ theme }) => theme.spacing.large}px;
  }
`;
const HeadingTitle = styled.h2 `
  margin-top: ${({ theme }) => theme.spacing.medium}px;
  font-size: ${({ theme }) => theme.fontSizes.large}px;
  font-weight: ${({ theme }) => theme.fontWeights.light};
  line-height: ${({ theme }) => theme.lineHeights.large};
  text-align: center;

  @media ${query.greaterThanMedium} {
    margin-left: ${({ theme }) => theme.spacing.medium}px;
    margin-top: 0;
    text-align: left;
  }
`;
const SummaryHeadingImage = styled(ModalImage) `
  margin: 0 auto;
`;
const ReplacementCardLink = styled(Link) `
  display: inline;
  padding: 0;
  color: ${({ theme }) => theme.colors.cyanBlue};
  font-weight: ${({ theme }) => theme.fontWeights.bold};

  &:focus {
    outline: 2px solid ${({ theme }) => theme.colors.cyanBlue};
  }
`;
export const SummaryHeading = () => {
    const { isLessThanTablet } = useWindowSize();
    const imageSize = isLessThanTablet ? 'xxsmall' : 'xsmall';
    return (_jsxs(HeadingWrapper, { children: [_jsx(SummaryHeadingImage, { imageName: "MAGNIFY", size: imageSize }), _jsx(HeadingTitle, { children: t('summaryDetails.pleaseReview') })] }));
};
const Subtitle = styled.h3 `
  padding-bottom: ${({ theme }) => theme.spacing.medium}px;
  border-bottom: 1px solid ${({ theme }) => theme.colors.lightGrey};
  font-size: ${({ theme }) => theme.fontSizes.medium}px;
  font-weight: ${({ theme }) => theme.fontWeights.light};
  line-height: ${({ theme }) => theme.lineHeights.medium};
`;
const CardholdersDescription = styled.p `
  margin-top: ${({ theme }) => theme.spacing.base}px;
  font-size: ${({ theme }) => theme.fontSizes.root}px;
  line-height: ${({ theme }) => theme.lineHeights.root};
`;
const CardHolderList = styled.ul `
  padding: unset;
`;
const CardholderWrapper = styled.li `
  list-style: none;
  margin-top: ${({ theme }) => theme.spacing.base}px;
`;
const CardholderName = styled.p `
  font-size: ${({ theme }) => theme.fontSizes.regular}px;
  font-weight: ${({ theme }) => theme.fontWeights.bold};
  line-height: ${({ theme }) => theme.lineHeights.small};
`;
const CardholderAccount = styled.p `
  font-size: ${({ theme }) => theme.fontSizes.small}px;
  line-height: ${({ theme }) => theme.lineHeights.small};
`;
export const RequestDetails = ({ cardholders, accountName }) => {
    return (_jsxs(_Fragment, { children: [_jsx(Subtitle, { children: t('summaryDetails.requestDetails') }), _jsx(CardholdersDescription, { children: t(`summaryDetails.theFollowingCardholders`, {
                    accountName
                }) }), _jsx(CardHolderList, { children: cardholders.map(({ id, name, last4digits }) => (_jsxs(CardholderWrapper, { children: [_jsx(CardholderName, { children: name }), _jsx(CardholderAccount, { children: t('common.accountEndingIn', { last4digits }) })] }, id))) })] }));
};
export const BoldText = styled.span `
  font-weight: ${({ theme }) => theme.fontWeights.bold};
`;
const DetailBlockParagraph = styled.p `
  white-space: pre-line;

  &:nth-of-type(1) {
    margin-top: ${({ theme }) => theme.spacing.xsmall}px;
  }
`;
const ShippingBlockParagraph = styled.p `
  margin-top: ${({ theme }) => theme.spacing.xsmall}px;
  white-space: pre-line;

  &:nth-of-type(2) {
    margin-top: 0px;
  }
`;
const Address = styled.div `
  margin: ${({ theme }) => theme.spacing.base}px 0;
`;
const WrongAddress = styled.p `
  white-space: pre-line;
  font-size: ${({ theme }) => theme.fontSizes.regular}px;
  line-height: ${({ theme }) => theme.lineHeights.small};
`;
export const DeliveryDetailBlock = ({ heading, paragraphs }) => (_jsxs("div", { children: [_jsx(BoldText, { children: heading }), paragraphs.map((paragraph, index) => (
        // eslint-disable-next-line react/no-array-index-key
        _jsx(DetailBlockParagraph, { children: paragraph }, `paragraph-${index}`)))] }));
export const ShippingDetailBlock = ({ heading, paragraphs }) => (_jsxs("div", { children: [_jsx(BoldText, { children: heading }), paragraphs.map((paragraph, index) => (
        // eslint-disable-next-line react/no-array-index-key
        _jsx(ShippingBlockParagraph, { children: paragraph }, `paragraph-${index}`)))] }));
export const DeliveryDetails = () => {
    const { stepsDispatch } = useSteps();
    const handleManageProfile = () => {
        stepsDispatch({ type: 'updateStep', payload: 'ExitConfirmation' });
    };
    const { config, accountId } = useConfig();
    const { data } = useProfileQuery({
        config,
        keyProps: { accountId, accountIdType: 'guid' }
    });
    const shippingAddress = (data === null || data === void 0 ? void 0 : data.profile.mailingAddress)
        ? formatAddress(data.profile.mailingAddress)
        : '';
    return (_jsxs(_Fragment, { children: [_jsx(Subtitle, { children: t('summaryDetails.deliveryDetails') }), _jsx(Stack, { size: "base" }), _jsx(DeliveryDetailBlock, { heading: t('summaryDetails.deliveryMethod'), paragraphs: [
                    t('summaryDetails.standardDelivery'),
                    t('summaryDetails.expectedDelivery'),
                    t('summaryDetails.freeShipping')
                ] }), _jsx(Stack, { size: "large" }), _jsx(ShippingDetailBlock, { heading: t('summaryDetails.shippingAddress'), paragraphs: [
                    t('summaryDetails.yourReplacementWillBe'),
                    _jsx(Address, { children: shippingAddress })
                ] }), _jsx(WrongAddress, { children: _jsxs(_Fragment, { children: [t('summaryDetails.wrongAddress'), _jsx(InlineCtaButton, Object.assign({ onClick: handleManageProfile, "data-cy": "manage-profile-button", "data-type": "replacement card", "data-reason": "manage profile", "data-object": "link", "data-analytics": "clicktrackingevent" }, { children: t('summaryDetails.manageProfile') })), t('summaryDetails.beforeSubmitting')] }) })] }));
};
const NoteWrapper = styled.p `
  padding: ${({ theme }) => theme.spacing.base}px;
  background-color: ${({ theme }) => theme.colors.greyNinetyEight};
  font-size: ${({ theme }) => theme.fontSizes.regular}px;
  line-height: ${({ theme }) => theme.lineHeights.small};
`;
export const Note = ({ noteMessage }) => {
    return (_jsxs(NoteWrapper, Object.assign({ "data-cy": "note-wrapper" }, { children: [_jsx(BoldText, { children: t('summaryDetails.noteTitle') }), noteMessage] })));
};
const CardBannerWrapper = styled.div `
  display: flex;
  padding: ${({ theme }) => `${theme.spacing.base}px ${theme.spacing.medium}px`};
  background-color: ${({ theme }) => theme.colors.turquoiseLightest};
  border-radius: 8px;

  @media ${query.greaterThanMedium} {
    max-width: 419px;
  }
`;
const DigitalWalletImage = styled.img `
  width: 64px;
  height: 64px;
  flex-basis: 64px;
  flex-shrink: 0;
  flex-grow: 0;
`;
const DigitalCardParagraph = styled.p `
  font-size: ${({ theme }) => theme.fontSizes.regular}px;
  line-height: ${({ theme }) => theme.lineHeights.small};
`;
const DigitalCardBodyWrapper = styled.div `
  margin-left: ${({ theme }) => theme.spacing.medium}px;
`;
export const DigitalCardBanner = () => {
    const { config: { hosts: { assets: assetsHost } } } = useConfig();
    const { isLessThanTablet } = useWindowSize();
    const devicePath = isLessThanTablet ? 'mobile/' : 'desktop/';
    const fileName = IMAGE_MAP.DIGITAL_WALLET;
    const imageSource = `${getAssetUrl(assetsHost)}${devicePath}${fileName}`;
    const { cardAccessUrl } = useCardAccessLink();
    return (_jsxs(CardBannerWrapper, Object.assign({ "data-cy": "digital-card-banner" }, { children: [_jsx(DigitalWalletImage, { src: imageSource }), _jsxs(DigitalCardBodyWrapper, { children: [_jsx(BoldText, { children: t('summaryDetails.payEasily') }), _jsx(Stack, { size: "small" }), _jsx(DigitalCardParagraph, { children: _jsxs(_Fragment, { children: [_jsx(ReplacementCardLink, Object.assign({ "data-type": "replacement card", "data-reason": "add to your digital wallet now", "data-object": "link", "data-analytics": "clicktrackingevent", href: cardAccessUrl, target: "_blank" }, { children: t('summaryDetails.addToYourDigital') })), t('summaryDetails.secureWay')] }) })] })] })));
};
//# sourceMappingURL=index.js.map